/**
 * Plain-language “pages” a role can use in the app (for Settings → role change preview).
 * Mirrors §9.6 / usePermissions thresholds; keep labels user-facing, not internal flags.
 */
import type { OrgRole } from '@core/types';

/** Ordered list of navigable areas (approx. sidebar + billing). */
const ALL_PAGES: readonly string[] = [
  'Dashboard',
  'Applications (full pipeline)',
  'Smart Match',
  'Candidates',
  'Jobs & clients',
  'Requisitions',
  'Interviews & AI interviews',
  'Email activity',
  'Reports & exports',
  'Activities & assignments',
  'Team directory',
  'Settings (org: team, roles, integrations)',
  'Masking templates',
  'Billing & credits',
];

/** Which pages each role can access (superset for preview). */
const ROLE_PAGES: Record<OrgRole, ReadonlySet<string>> = {
  owner: new Set(ALL_PAGES),
  /** Admin: same as owner except billing ownership (credits / plan purchase). */
  admin: new Set(ALL_PAGES.filter((p) => p !== 'Billing & credits')),
  senior_recruiter: new Set([
    'Dashboard',
    'Applications (full pipeline)',
    'Smart Match',
    'Candidates',
    'Jobs & clients',
    'Requisitions',
    'Interviews & AI interviews',
    'Email activity',
    'Reports & exports',
    'Activities & assignments',
    'Team directory',
    'Settings (profile & preferences)',
    'Masking templates',
  ]),
  junior_recruiter: new Set([
    'Dashboard',
    'Applications (assigned / create)',
    'Smart Match',
    'Candidates (create & edit)',
    'Jobs & clients (view)',
    'Requisitions (view)',
    'Interviews & AI interviews',
    'Email activity',
    'Activities & assignments',
    'Team directory',
    'Settings (profile & preferences)',
  ]),
  viewer: new Set([
    'Dashboard',
    'Applications (limited)',
    'Candidates (lightweight metadata)',
    'Jobs & clients (view)',
    'Interviews & AI interviews',
    'Settings (profile & preferences)',
  ]),
};

export function pagesForRole(role: OrgRole): ReadonlySet<string> {
  return ROLE_PAGES[role] ?? new Set();
}

export function diffRolePages(from: OrgRole, to: OrgRole): { gain: string[]; lose: string[] } {
  const A = pagesForRole(from);
  const B = pagesForRole(to);
  const gain: string[] = [];
  const lose: string[] = [];
  for (const p of B) {
    if (!A.has(p)) gain.push(p);
  }
  for (const p of A) {
    if (!B.has(p)) lose.push(p);
  }
  gain.sort((a, b) => a.localeCompare(b));
  lose.sort((a, b) => a.localeCompare(b));
  return { gain, lose };
}
